import { useEffect, useMemo, useState } from 'react'

import { useTranslation } from 'react-i18next'
import styled from 'styled-components'

import avatarPlaceholder from 'assets/images/user_placeholder.png'
import { IconModalClose } from 'assets/svg/sprite'
import UserDetails from 'components/UI/Modal/UserDetails/UserDetails'
import { Portal } from 'components-ref/atoms'
import { useDisableBodyScroll } from 'helpers/hooks/useDisableBodyScroll/useDisableBodyScroll'
import colors from 'styles/colors'
import { type IUserCard } from 'types/interfaces/IUserCard'

export const UserNoOptionsCard = ({
  item,
  style
}: {
  item: IUserCard
  style: React.CSSProperties
}) => {
  const { t } = useTranslation()
  const [open, setOpen] = useState(false)

  const userForDetails = useMemo(
    () => ({
      ...item,
      display_name: item.name,
      avatar: {
        url: item?.avatar_photo?.src ?? item?.avatar?.url ?? avatarPlaceholder
      }
    }),
    [item]
  )

  const avatarSrc =
    item?.avatar_photo?.src?.trim() ||
    item?.avatar?.url?.trim() ||
    avatarPlaceholder

  return (
    <>
      <RowItem style={style}>
        <ViewerContainer onClick={() => setOpen(true)}>
          <Avatar>
            <img
              src={avatarSrc}
              alt={t('avatar')}
              loading="lazy"
              decoding="async"
              referrerPolicy="no-referrer"
              onError={(e) => {
                const img = e.currentTarget
                if (img.src !== avatarPlaceholder) img.src = avatarPlaceholder
              }}
            />
          </Avatar>
          <ViewerDetails>
            <ViewerName title={item.name}>{item.name}</ViewerName>
            <ViewerUsername title={`@${item.username}`}>@{item.username}</ViewerUsername>
          </ViewerDetails>
        </ViewerContainer>
      </RowItem>

      {open ? (
        <UserDetailsTopModal
          onClose={() => setOpen(false)}
        >
          <UserDetails user={userForDetails} />
        </UserDetailsTopModal>
      ) : null}
    </>
  )
}

const UserDetailsTopModal = ({
  children,
  onClose
}: {
  children: React.ReactNode
  onClose: () => void
}) => {
  useDisableBodyScroll()

  useEffect(() => {
    const isOpen = document.body.classList.contains('portal--open')
    if (!isOpen) document.body.classList.add('portal--open')

    return () => {
      if (!isOpen) document.body.classList.remove('portal--open')
    }
  }, [])

  return (
    <Portal>
        <TopLayer
          onMouseDown={(e) => {
            e.preventDefault()
            e.stopPropagation()
            if (e.nativeEvent?.stopImmediatePropagation) {
              e.nativeEvent.stopImmediatePropagation()
            }
            onClose()
          }}
        >
        <Card
          onClick={(e) => {
            // clicks inside card should not close it
            e.preventDefault()
            e.stopPropagation()
          }}
        >
        <CloseButton
          type="button"
          onMouseDown={(e) => {
            e.preventDefault()
            e.stopPropagation()
            onClose()
          }}
        >
          <IconModalClose color="#A3A3A3" />
        </CloseButton>

          <CardBody>{children}</CardBody>
        </Card>
      </TopLayer>
    </Portal>
  )
}

const TopLayer = styled.div`
  position: fixed;
  inset: 0;
  z-index: 2147483647;
  padding: 2rem;

  display: flex;
  justify-content: center;
  align-items: center;

  height: 100vh;
  overflow: hidden;
`

const Card = styled.div`
  position: relative;
  width: 100%;
  max-width: 70rem;

  max-height: 80vh;
  overflow: hidden;

  border-radius: 2rem;
  background: #fff;

  box-shadow: 0 0.4rem 5.4rem rgba(0, 0, 0, 0.25);

  display: flex;
  flex-direction: column;
`

const CloseButton = styled.button`
  position: absolute;
  top: 1rem;
  right: 2rem;
  z-index: 10;

  cursor: pointer;
  background: transparent;
  border: none;
  padding: 0;

  svg {
    width: 1.4rem;
    height: 1.4rem;
  }
`

const CardBody = styled.div`
  min-height: 0;
`


const RowItem = styled.div`
  padding: 5px 0px;
  width: 99% !important;
  height: 90%;
`

const ViewerContainer = styled.div`
  display: flex;
  align-items: center;
  width: 100%;
  height: 100%;
  background: ${colors.white89deg};
  box-shadow: 0 0.4rem 2rem rgba(0, 0, 0, 0.05);
  border-radius: 2.1rem;
  padding: 1.2rem 1rem;
  margin-top: 5px;
`

const Avatar = styled.div`
  width: 3.6rem;
  height: 3.6rem;
  overflow: hidden;
  border-radius: 50%;
  margin-right: 1rem;

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    object-position: center;
    display: block;
  }
`

const ViewerDetails = styled.div`
  margin-right: auto;
`

const ViewerName = styled.div`
  color: #0d1444;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  margin-right: 1rem;
  font-size: 1.4rem;
  font-weight: 600;
`

const ViewerUsername = styled.div`
  color: #828c94;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  font-size: 1.2rem;
  font-weight: 500;
`